import styled from 'styled-components';
import { Select, TreeSelect, SelectProps } from 'antd';
import { media } from 'utils/styledHelpers';
import { StyledLabelText } from 'components/styledComponents/input/Input.styled';

type SizeType = SelectProps['size'];

interface StyledAutoRefreshProps {
  $fullscreen: boolean;
}
interface StyledSelectWrapperProps {
  $hideItemRemove?: boolean;
  $disabled?: boolean;
  $maxTagCount?: number;
}
interface StyledSelectInTableProps {
  $wide?: boolean;
  $isFullWidth?: boolean;
}
interface StyledSelectOptionProps {
  $isBetween?: boolean;
}
interface StyledMultiSelectWithSeparatorProps {
  $isFullWidth?: boolean;
}
interface StyledDraggableSelectWrapperProps {
  $listHeight?: number;
}
interface StyledDraggableOptionProps {
  $isFixed?: boolean;
}
interface StyledDraggableOptionSelectAllProps {
  $isDragEnabled?: boolean;
  $isSelected?: boolean;
  $isSticky?: boolean;
}
interface StyledDraggableOptionDragProps {
  $isDraggable?: boolean;
}
interface StyledDraggableOptionLabelProps {
  $disabled?: boolean;
  $isSelected?: boolean;
}

const getSelectHeight = (size?: SizeType) => {
  const sizes = {
    small: '2rem',
    middle: '2.29rem',
    large: '2.57rem',
  };

  return size ? sizes[size] : sizes.middle;
};

const StyledSelectWrapper = styled(Select)<SelectProps & StyledSelectWrapperProps>`
  width: 100%;
  &.ant-select-show-search.ant-select-single:not(.ant-select-customize-input) .ant-select-selector {
    cursor: pointer;
    background-color: transparent;
    border-radius: ${(props) => props.theme.customBorderRadius.borderRadiusSmall};
    line-height: inherit;
  }
  .ant-select-dropdown {
    overflow: hidden;
    background-color: ${(props) => props.theme.colors.selectBg};

    .ant-select-dropdown-menu {
      padding: 0;
    }
  }

  &.ant-select-focused {
    position: relative;
  }
  &.ant-select-single.ant-select-show-arrow .ant-select-selection-search {
    right: 4.14rem;
  }

  &.ant-select-multiple.ant-select-allow-clear .ant-select-selector {
    padding-right: 4.5rem !important;
    border-radius: ${(props) => props.theme.customBorderRadius.borderRadiusSmall};
  }

  &.ant-select .ant-select-clear {
    background: transparent;
  }

  &.ant-select {
    height: ${(props) => getSelectHeight(props.size)};
    line-height: ${(props) => getSelectHeight(props.size)};
    cursor: ${(props) => (props.$disabled ? 'not-allowed' : 'pointer')};
    &.ant-select-open .ant-select-selection,
    &:hover .ant-select-selection {
      border-color: ${(props) => props.theme.colors.blue};
      box-shadow: initial !important;
    }

    .ant-select-selection--single,
    .ant-select-selection__rendered,
    .ant-select-selection--multiple,
    .ant-select-selector {
      height: 100%;
      min-height: initial;
    }
    .ant-select-content-value {
      font-size: ${(props) => props.theme.typography.fontSizeDefault};
    }
    .ant-select-content {
      .ant-select-selection-item {
        background-color: transparent;
        border: none;
        margin: 0;
        height: inherit;

        &-remove:hover {
          color: ${(props) => props.theme.colors.primaryText};
        }
        &-content {
          white-space: nowrap;
          font-size: ${(props) => props.theme.typography.fontSizeDefault};
        }
      }
    }
    .ant-select {
      width: 100%;
    }
    .ant-select-arrow {
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      margin-top: 0;
      height: auto;
      opacity: 1;
      svg {
        width: 0.7rem;
        height: 0.9rem;
      }
    }
    &.ant-select-allow-clear:hover {
      .ant-select-suffix {
        opacity: 0;
      }
    }
  }
  &.ant-select-multiple {
    background-color: transparent;
    flex-wrap: initial;
    cursor: ${(props) => (props.$disabled ? 'not-allowed' : 'pointer')};
    pointer-events: ${(props) => (props.$disabled ? 'none' : 'initial')};
    border-color: ${(props) => props.theme.colors.primaryBorder};
    &:hover {
      border-color: ${(props) => props.theme.colors.blue};
    }
    .ant-select-content-item {
      &:not(.ant-select-content-item-rest):not(.ant-select-content-item-suffix) {
        max-width: ${(props) => (props.value && Array.isArray(props.value) && (props.value.length > Number(props?.$maxTagCount)) ? `calc((100% / ${Number(props?.$maxTagCount) + 1}) - 1%)` : '100%')};
      }

      .ant-select-selection-item-remove {
        display: ${(props) => props.$hideItemRemove && 'none'};
      }
    }
  }
` as typeof Select;

const StyledUserManagementSelectWrapper = styled(StyledSelectWrapper)`
  &.ant-select {
    height: 2.57rem;
  }
`;

const StyledSelectInTable = styled.div<StyledSelectInTableProps>`
  max-width: ${(props) => (props.$wide ? '20rem' : '14rem')};

  .ant-col {
    border: none !important;
  }
  .ant-select {
    width: ${(props) => (props.$isFullWidth ? '100%' : '')};
    .ant-select-selector {
      background-color: ${(props) => props.theme.colors.selectBg} !important;
    }
  }
`;

const StyledSelectOption = styled.div<StyledSelectOptionProps>`
  display: flex;
  align-items: center;
  justify-content: ${(props) => (props.$isBetween ? 'space-between' : 'flex-start')};
  gap: 0.6rem;
  height: 100%;
  max-width: 100%;
`;

const StyledSelectOptionWithExtra = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const StyledSelectOptionExtra = styled.div`
  background: ${(props) => props.theme.colors.primaryBg};
  padding: 0 0.5rem;
  border-radius: ${(props) => props.theme.customBorderRadius.borderRadiusSmall};
  height: 1.5rem;
  line-height: 1.5rem;
  margin-left: 0.85rem;
  max-width: 50%;
  > div {
    font-size: 0.85rem;
  }
`;

const StyledHiddenOptionExtraData = styled.div`
  .ant-select-content {
    .ant-select-selection-item-content div + div,
    ${StyledSelectOptionExtra} {
      display: none;
    }
  }
`;

const StyledCustomSelect = styled.div`
  max-height: 18rem;
  padding: 0 4px;
  overflow: auto;
`;
const StyledCustomSelectOption = styled(StyledSelectOption)`
  min-height: 32px;
  padding: 0.57rem;
  border-radius: ${(props) => props.theme.customBorderRadius.borderRadiusDefault};
  transition: 0.3s background-color;
  cursor: pointer;

  &:hover {
    background-color: #cce2fa;
  }
`;

const StyledMultiSelectWithSeparator = styled.div<StyledMultiSelectWithSeparatorProps>`
  position: relative;
  max-width: ${(props) => (props.$isFullWidth ? 'auto' : '20rem')};
  .ant-col {
    position: initial;
  }
  .ant-space-compact {
    width: 100%;
    .ant-form-item {
      position: initial;
      width: 50%;

      .ant-select.ant-select-single {
        height: 2.58rem;
        border-color: ${(props) => props.theme.colors.primaryBorder};

        .ant-select-selector {
          padding: 0 0.71rem;

          .ant-select-selection-placeholder,
          .ant-select-selection-item {
            padding-right: 0.57rem;
          }

          .ant-select-selection-search {
            width: calc(100% - 3rem);
          }
        }
      }

      &:first-child {
        .ant-select.ant-select-single {
          border-right-width: 0;
          border-top-right-radius: 0;
          border-bottom-right-radius: 0;
        }
      }

      &:last-child {
        .ant-select.ant-select-single {
          border-left-width: 0;
          border-top-left-radius: 0;
          border-bottom-left-radius: 0;
        }
      }

      .ant-form-item-control-input {
        height: 100%;
      }

      .ant-form-item-explain {
        z-index: 555;
        top: 100%;
        &-error {
          height: auto;
        }
      }
      .ant-form-item-explain-error {
        position: relative;
        z-index: 99;
        width: calc(200% + 3.42rem);
      }
    }
    .ant-input {
      background: ${(props) => props.theme.colors.selectBg} !important;
    }
  }
`;

const StyledMultiSelectWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;

  ${StyledLabelText} {
    flex-basis: 100%;
  }

  .ant-form-item {
    &:first-of-type {
      flex-basis: 35%;
      width: 35%;
      .ant-select {
          border-bottom-right-radius: 0;
          border-top-right-radius: 0;
          border-right-color: transparent;
      }
    }

    &:last-of-type {
      flex-grow: 1;
      .ant-select {
          border-bottom-left-radius: 0;
          border-top-left-radius: 0;
      }
    }
  }
`;

const StyledAutoRefresh = styled.div<StyledAutoRefreshProps>`
  padding-left: 1.14rem;
  margin-left: 1.14rem;
  border-left: 1px solid ${(props) => props.theme.colors.secondaryBorder};

  ${media.xs`
    padding-left: 0;
    margin-left: 0;
    border: none;
  `}

  .ant-form-item {
    .ant-row {
      align-items: center;
      .ant-col.ant-form-item-label,
      .ant-col.ant-form-item-control {
        flex: 0 0 auto;
        padding: 0;
        line-height: 1;
      }
      .ant-select {
        width: 10rem;
        margin-left: ${(props) => !props.$fullscreen && '1rem'};
      }
    }
  }
`;

const StyledTreeSelect = styled(TreeSelect)`
  background: ${(props) => props.theme.colors.secondaryBg};
  border: 1px solid ${(props) => props.theme.colors.primaryBorder};
  
  .ant-select-arrow svg {
    fill: ${(props) => props.theme.colors.primaryText};
  }
  
  & + .ant-select-dropdown {
    .ant-select-tree {
      color: ${(props) => props.theme.colors.primaryText};
      background: ${(props) => props.theme.colors.selectBg};
      
      .ant-select-tree-treenode {
        .ant-select-tree-node-content-wrapper {
          &, &:hover {
            background: ${(props) => props.theme.colors.selectBg};
            color: ${(props) => props.theme.colors.primaryText};
          }
        }
      } 
    }
  
  }
`;

const StyledSelectInManageFilters = styled.div`
  margin-bottom: 0.57rem;
  display: flex;
  align-items: center;
  column-gap: 1.71rem;
  font-size: ${(props) => props.theme.typography.fontSizeDefault};

  &:not(:last-child) {
    border-right: 1px solid ${(props) => props.theme.colors.secondaryBorder};
    padding-right: 1.14rem;
    ${media.xs`
       border-right: none;
       padding-right: 0;
       width: 100%;
       align-items: flex-end;
       .ant-form-item {
            width: 100%;
            flex-wrap: nowrap;
       }
    `}
  }

  .ant-form-item {
    .ant-row {
        align-items: center;
    }
    .ant-form-item-label {
      padding-bottom: 0;
    }
  }
  .ant-select {
    width: 10rem;
    ${media.xs`
      width: 100%;
      min-width: 10rem;
    `}
    &[iswide] {
      width: 16rem;
      ${media.xs`
        width: 100%;
      `}
    }]
  }

  ${media.sm`
    border: none;
    margin-right: 0;
    padding-right: 0;
    column-gap: 1.14rem;
    .ant-form {
      .ant-form-item {
        .ant-col.ant-form-item-label,
        .ant-col.ant-form-item-control {
          flex: 0 0 auto;
        }
      }
    }
  `}

  ${media.xs`
    width: 100%;
    .ant-form-item {
      width: 100%;
    }
    ${StyledHiddenOptionExtraData} {
      width: 100%;
    }
  `}
`;

const StyledInputSearch = styled.div`
  .ant-input-group-wrapper .ant-input-group > .ant-input-group-addon > button {
    background: transparent;
  }
`;

const StyledDraggableSelectWrapper = styled.div<StyledDraggableSelectWrapperProps>`
  max-height: ${(props) => props.$listHeight}px;
  overflow: hidden auto;
`;

const StyledDraggableOption = styled.div<StyledDraggableOptionProps>`
  min-height: 32px;
  padding: 8px 12px;
  font-weight: normal;
  font-size: 14px;
  line-height: 1.2;
  box-sizing: border-box;
  cursor: ${(props) => (props.$isFixed ? 'not-allowed' : 'pointer')};
  transition: background 0.3s ease;
  border-radius: 4px;
  display: flex;
  gap: 12px;

  &:hover {
    background: ${(props) => props.theme.colors.hoverBg};
  }
`;

const StyledDraggableOptionSelectAll = styled(StyledDraggableOption)<StyledDraggableOptionSelectAllProps>`
  padding-left: ${(props) => (props.$isDragEnabled ? 'calc(24px + 1.29rem)' : '24px')};
  font-weight: ${(props) => (props.$isSelected ? props.theme.typography.fontWeightMedium : 'inherit')};
  .ant-checkbox-wrapper {
    gap: 4px;
  }

  ${(props) => props.$isSticky
    && `
    & {
      position: sticky;
      z-index: 9;
      top: 0;
    }
    &, &:hover {
      background-color: ${props.theme.colors.selectBg};
    }
  `}
`;

const StyledDraggableOptionDrag = styled.div<StyledDraggableOptionDragProps>`
  cursor: ${(props) => (props.$isDraggable ? 'grab' : 'not-allowed')};
  opacity: ${(props) => (props.$isDraggable ? 1 : 0.5)};
`;

const StyledDraggableOptionContent = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  min-width: 0;
  cursor: 'pointer';
`;

const StyledDraggableOptionLabel = styled.span<StyledDraggableOptionLabelProps>`
  font-size: ${(props) => props.theme.typography.fontSizeDefault};
  color: ${(props) => (!props.$disabled ? props.theme.colors.primaryText : 'inherit')};
  font-weight: ${(props) => (props.$isSelected ? props.theme.typography.fontWeightMedium : 'inherit')};
`;

export {
  StyledSelectWrapper,
  StyledUserManagementSelectWrapper,
  StyledSelectInTable,
  StyledSelectOption,
  StyledHiddenOptionExtraData,
  StyledCustomSelect,
  StyledCustomSelectOption,
  StyledMultiSelectWithSeparator,
  StyledMultiSelectWrapper,
  StyledSelectOptionWithExtra,
  StyledSelectOptionExtra,
  StyledAutoRefresh,
  StyledTreeSelect,
  StyledSelectInManageFilters,
  StyledInputSearch,
  StyledDraggableOption,
  StyledDraggableOptionDrag,
  StyledDraggableOptionContent,
  StyledDraggableOptionLabel,
  StyledDraggableOptionSelectAll,
  StyledDraggableSelectWrapper,
};
